/**
 * Il menu laterale.
 *
 * Nasce da una previsione: **le voci cresceranno**. Una barra in cima regge
 * otto voci e smette di reggerne dodici — vanno a capo, si stringono, e la
 * riga diventa un muro di parole tutte uguali. Di lato lo spazio verticale non
 * finisce, e soprattutto si possono **raggruppare**: è il raggruppamento, non
 * la posizione, a rendere leggibile un menu che cresce.
 *
 * I gruppi seguono il flusso della merce — ricezione, magazzino, produzione,
 * spedizione — perché è l'ordine in cui la merce attraversa l'azienda, e quindi
 * l'unico ordine che chi ci lavora non deve imparare.
 *
 * **Si chiude**, e la scelta resta. In reparto lo schermo è quello che è, e una
 * postazione che fa un gesto solo tutto il giorno non ha bisogno di vedere le
 * altre sette. Chiuso resta la sigla, non un'icona: questa interfaccia è
 * tipografica, e tre lettere si leggono a un metro meglio di un pittogramma che
 * va indovinato.
 */

import { useEffect, useState } from 'react';

const CHIAVE = 'errebi:menu-chiuso';

export interface VoceMenu {
  readonly k: string;
  readonly nome: string;
  /** Due o tre lettere: quello che resta quando il menu è chiuso. */
  readonly sigla: string;
  readonly gruppo: string;
}

/**
 * L'ordine dei gruppi è quello in cui la merce attraversa l'azienda.
 *
 * «Guida» sta in coda e fuori da quel filo, ed è l'eccezione che conferma a cosa
 * serve l'elenco: i primi quattro sono momenti della merce, il quinto è il piede
 * della colonna. Un gruppo che non compare qui ha la rotta funzionante e la voce
 * invisibile — è il modo più silenzioso di aggiungere una schermata che nessuno
 * troverà.
 */
export const GRUPPI: readonly string[] = [
  'Ricezione',
  'Magazzino',
  'Produzione',
  'Spedizione',
  'Guida',
];

function leggiChiuso(): boolean {
  try {
    // Su uno schermo stretto si parte chiusi: meglio poco menu e tutta la
    // schermata, che il contrario.
    if (localStorage.getItem(CHIAVE) === null) return window.innerWidth < 1100;
    return localStorage.getItem(CHIAVE) === 'si';
  } catch {
    return false;
  }
}

export function MenuLaterale({
  voci,
  corrente,
  vai,
  utente,
  esci,
}: {
  voci: readonly VoceMenu[];
  corrente: string | undefined;
  vai: (k: string) => void;
  utente: string;
  esci: () => void;
}) {
  const [chiuso, setChiuso] = useState(leggiChiuso);

  useEffect(() => {
    try {
      localStorage.setItem(CHIAVE, chiuso ? 'si' : 'no');
    } catch {
      // niente localStorage (finestra privata): la scelta vale per questa sessione
    }
  }, [chiuso]);

  const gruppiPresenti = GRUPPI.filter((g) => voci.some((v) => v.gruppo === g));

  return (
    <nav className={`menu${chiuso ? ' menu--chiuso' : ''}`} aria-label="Reparti">
      <button
        className="menu__leva"
        onClick={() => setChiuso((c) => !c)}
        aria-expanded={!chiuso}
        title={chiuso ? 'Apri il menu' : 'Chiudi il menu'}
      >
        <span className="menu__leva-segno" aria-hidden="true">
          {chiuso ? '»' : '«'}
        </span>
        {!chiuso && <span className="menu__leva-testo">Chiudi</span>}
      </button>

      <div className="menu__corpo">
        {gruppiPresenti.map((gruppo) => (
          <div className="menu__gruppo" key={gruppo}>
            {/* Chiuso il titolo sparisce ma il gruppo resta separato da una
                riga: la struttura si legge anche senza le parole. */}
            {chiuso ? <hr className="menu__taglio" /> : <h2 className="menu__titolo">{gruppo}</h2>}

            {voci
              .filter((v) => v.gruppo === gruppo)
              .map((v) => (
                <button
                  key={v.k}
                  className={`menu__voce${corrente === v.k ? ' menu__voce--qui' : ''}`}
                  onClick={() => vai(v.k)}
                  aria-current={corrente === v.k ? 'page' : undefined}
                  title={chiuso ? v.nome : undefined}
                >
                  <span className="menu__sigla" aria-hidden={!chiuso}>
                    {v.sigla}
                  </span>
                  {!chiuso && <span className="menu__nome">{v.nome}</span>}
                </button>
              ))}
          </div>
        ))}
      </div>

      <div className="menu__piede">
        {!chiuso && <span className="menu__chi">{utente}</span>}
        <button className="menu__esci" onClick={esci} title="Esci">
          {chiuso ? '⏻' : 'Esci'}
        </button>
      </div>
    </nav>
  );
}
